import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Gift, Loader2, RotateCcw, Share2, Smartphone, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/auth";
import { vibrate } from "@/lib/quantum";
import { Page } from "@/components/app/AppKit";
import { LivingAnkh } from "@/components/app/AnkhBreath";
import CoupleMap from "@/components/app/CoupleMap";
import { getFigures } from "@/lib/bodyRegions";

const ROUNDS = 9;
const give = ["out", "Give · exhale “HAH”", "Send the current over your crown, through your heart, to your partner"];
const take = ["in", "Receive · inhale", "Draw your partner’s current up your spine to the back heart"];
const CYCLES = {
  mirrored: [{ ms: 6000, a: take, b: give }, { ms: 6000, a: give, b: take }],
  unison: [
    { ms: 4000, a: ["in", "Inhale together", "Perineum rises, up the back spine"] },
    { ms: 2000, a: ["hold", "Hold together", "Your back hearts expand as one"] },
    { ms: 6000, a: ["out", "Exhale together · “HAH”", "Over the crown, through the heart, home"] },
  ],
};
const RHYTHMS = [["mirrored", "Mirrored", "One inhales as the other exhales, passing the current between you"], ["unison", "In unison", "Both breathe the Ankh together, as one body"]];
const btn = "inline-flex items-center justify-center gap-2 rounded-full py-3.5 px-5 font-tt-mono text-[12px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50";
const gold = `${btn} bg-[#D9B47A] text-[#0A0706] hover:bg-[#F2E8DF]`;
const ghost = `${btn} border border-[#D9B47A]/40 text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706]`;
const field = "w-full bg-[#0F070B] border border-[#D9B47A]/25 px-4 py-3 text-[16px] text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#E8838E]";

// Where both partners are at `elapsed` ms after the shared start.
const at = (rhythm, elapsed) => {
  const cycle = CYCLES[rhythm] ?? CYCLES.mirrored;
  const len = cycle.reduce((n, s) => n + s.ms, 0);
  if (elapsed < 0) return { countdown: Math.ceil(-elapsed / 1000) };
  if (elapsed >= len * ROUNDS) return { done: true };
  let t = elapsed % len;
  let idx = 0;
  while (t >= cycle[idx].ms) t -= cycle[idx++].ms;
  const round = Math.floor(elapsed / len) + 1;
  const seg = cycle[idx];
  return { round, idx, ms: seg.ms, a: seg.a, b: seg.b ?? seg.a, beat: `${round}-${idx}` };
};

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);
  return now;
};

const RhythmPick = ({ value, onChange, disabled }) => (
  <div role="radiogroup" aria-label="Rhythm" className="mt-3 grid gap-2">
    {RHYTHMS.map(([k, l, s]) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} data-testid={`pb-rhythm-${k}`} disabled={disabled} onClick={() => onChange(k)}
        className={`text-left border p-4 transition-colors disabled:opacity-60 ${value === k ? "border-[#D9B47A] bg-[#1A120E]" : "border-[#D9B47A]/20 hover:border-[#D9B47A]/50"}`}>
        <span className="block font-display text-xl">{l}</span><span className="block mt-1 text-[13px] text-[#A8998C]">{s}</span>
      </button>
    ))}
  </div>
);

// The current passing between the two ankhs: flows from giver to receiver (mirrored) or glows in both (unison).
const Current = ({ state, rhythm }) => {
  const toRight = state.a[0] === "out";
  return (
    <svg viewBox="0 0 100 10" preserveAspectRatio="none" className="absolute left-[30%] right-[30%] top-[38%] h-3 w-[40%] overflow-visible pointer-events-none" aria-hidden>
      <line x1="0" y1="5" x2="100" y2="5" stroke="#D9B47A" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
      {rhythm === "mirrored" ? (
        <motion.circle key={state.beat} cy="5" r="3.5" fill="#F4D9A4" style={{ filter: "drop-shadow(0 0 6px rgba(244,217,164,0.9))" }}
          initial={{ cx: toRight ? 0 : 100, opacity: 0 }} animate={{ cx: toRight ? 100 : 0, opacity: [0, 1, 1, 0] }} transition={{ duration: state.ms / 1000, ease: "easeInOut" }} />
      ) : (
        <motion.line key={state.beat} x1="0" y1="5" x2="100" y2="5" stroke="#F4D9A4" strokeWidth="2" vectorEffect="non-scaling-stroke"
          initial={{ opacity: 0.1 }} animate={{ opacity: state.a[0] === "hold" ? 0.9 : [0.1, 0.6, 0.1] }} transition={{ duration: state.ms / 1000 }} />
      )}
    </svg>
  );
};

const Partner = ({ name, phase, me, beat, ms, testId }) => (
  <div data-testid={testId} className={`flex flex-col items-center text-center min-w-0 ${me === false ? "opacity-80" : ""}`}>
    <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A] truncate max-w-full">{name}{me ? " · you" : ""}</p>
    <LivingAnkh phase={phase[0]} beat={beat} ms={ms} className="mt-3 w-32 h-32 sm:w-40 sm:h-40" testId={`${testId}-ankh`} />
    <motion.p key={`${beat}-l`} initial={{ opacity: 0.3, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid={`${testId}-phase`} className="mt-4 font-display text-xl sm:text-2xl leading-tight">{phase[1]}</motion.p>
    <p className="mt-1 text-[13px] text-[#A8998C] leading-snug">{phase[2]}</p>
  </div>
);

const Duo = ({ rhythm, names, mine, elapsed }) => {
  const s = at(rhythm, elapsed);
  if (s.countdown) return <div data-testid="pb-countdown" className="mt-16 text-center"><p className="font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#A8998C]">Settle into each other’s eyes</p><motion.p key={s.countdown} initial={{ scale: 1.3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-6 font-display text-7xl text-[#D9B47A]">{s.countdown}</motion.p></div>;
  if (s.done) return null;
  return (
    <div data-testid="pb-duo" data-rhythm={rhythm} className="mt-10">
      <div className="relative grid grid-cols-2 gap-4">
        <Partner name={names[0]} phase={s.a} me={mine === null ? null : mine === 0} beat={s.beat} ms={s.ms} testId="pb-partner-a" />
        <Partner name={names[1]} phase={s.b} me={mine === null ? null : mine === 1} beat={s.beat} ms={s.ms} testId="pb-partner-b" />
        <Current state={s} rhythm={rhythm} />
      </div>
      <p data-testid="pb-round" className="mt-8 text-center font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">Round {s.round} of {ROUNDS} · {rhythm === "mirrored" ? "Mirrored" : "In unison"}</p>
    </div>
  );
};

// Shared hook: buzz on every phase change and log the practice once when the breath completes.
const useBreathLife = (rhythm, elapsed, running) => {
  const s = running ? at(rhythm, elapsed) : {};
  const last = useRef("");
  const logged = useRef(false);
  useEffect(() => {
    if (!s.beat || last.current === s.beat) return;
    last.current = s.beat;
    vibrate(40);
  }, [s.beat]);
  useEffect(() => {
    if (!s.done || logged.current) return;
    logged.current = true;
    api.post("/practice/breath", { rounds: ROUNDS }).catch(() => {});
  }, [s.done]);
  return s;
};

const Done = ({ map, children }) => (
  <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} data-testid="pb-done" className="mt-10 text-center">
    <div className="flex justify-center gap-6"><LivingAnkh phase="hold" beat="done-a" className="w-24 h-24" testId="pb-done-a" /><LivingAnkh phase="hold" beat="done-b" className="w-24 h-24" testId="pb-done-b" /></div>
    <p className="mt-8 font-display text-3xl leading-snug">Two circuits, <em className="text-[#D9B47A]">one current.</em></p>
    <p className="mt-3 text-[15px] text-[#CFC2B6]">Rest in what you have built together. Your shared breath is saved to your practice.</p>
    {map && <div className="mt-8 border border-[#D9B47A]/20 p-4 text-left"><p className="font-display text-2xl text-center">Where did you feel it?</p><p className="mt-1 mb-4 text-center text-[13px] text-[#A8998C]">Tap your own figure where the current lived. Tap again to brighten.</p>{map}</div>}
    <div className="mt-8 grid gap-3">{children}</div>
  </motion.section>
);

// Two phones: my taps save as I go; my partner's appear as the session refreshes.
const SharedMap = ({ s }) => {
  const me = s.is_host ? "host" : "guest";
  const [mine, setMine] = useState(() => s.maps?.[me] ?? {});
  const figures = useState(() => getFigures().pair)[0];
  const tap = (i, levels) => {
    setMine(levels);
    api.post(`/breath/partner/${s.code}/map`, { levels, figures }).catch((err) => toast.error(errText(err.response?.data?.detail)));
  };
  const maps = s.is_host ? [mine, s.maps?.guest ?? {}] : [s.maps?.host ?? {}, mine];
  return <><CoupleMap names={[s.host_name, s.guest_name || "Your partner"]} maps={maps} figures={s.maps?.[s.is_host ? "guest" : "host"] ? s.figures : figures} editable={[s.is_host, !s.is_host]} onChange={tap} testId="pb-couple-map" /><p data-testid="pb-map-saved" className="mt-3 text-center text-[12px] text-[#A8998C]">Saved to both your journals as you tap.</p></>;
};

// One phone: both partners tap, then save it to the journal.
const LocalMap = ({ names }) => {
  const [maps, setMaps] = useState([{}, {}]);
  const [saved, setSaved] = useState(false);
  const figures = useState(() => getFigures().pair)[0];
  const save = () => api.post("/breath/maps", { names, maps, figures }).then(() => { setSaved(true); toast.success("Your shared map is saved to your journal."); }).catch((err) => toast.error(errText(err.response?.data?.detail)));
  return (
    <>
      <CoupleMap names={names} maps={maps} figures={figures} editable={[true, true]} onChange={(i, v) => { setSaved(false); setMaps((m) => m.map((x, j) => (j === i ? v : x))); }} testId="pb-couple-map" />
      <button data-testid="pb-map-save" onClick={save} disabled={saved || !(Object.keys(maps[0]).length || Object.keys(maps[1]).length)} className={`${ghost} w-full mt-4`}>{saved ? "Saved to your journal" : "Save to journal"}</button>
    </>
  );
};

const SideBySide = () => {
  const now = useNow();
  const [names, setNames] = useState(["", ""]);
  const [rhythm, setRhythm] = useState("mirrored");
  const [startAt, setStartAt] = useState(null);
  const shown = [names[0].trim() || "Partner one", names[1].trim() || "Partner two"];
  const s = useBreathLife(rhythm, startAt ? now - startAt : 0, Boolean(startAt));
  if (startAt && s.done) return <Done map={<LocalMap names={shown} />}><button data-testid="pb-local-again" onClick={() => setStartAt(Date.now() + 4000)} className={gold}><RotateCcw className="w-4 h-4" /> Breathe again</button><button data-testid="pb-local-reset" onClick={() => setStartAt(null)} className={ghost}>Change rhythm</button></Done>;
  if (startAt) return <><Duo rhythm={rhythm} names={shown} mine={null} elapsed={now - startAt} /><button data-testid="pb-local-stop" onClick={() => setStartAt(null)} className="mt-10 mx-auto flex items-center gap-2 text-sm text-[#A8998C] underline underline-offset-4"><X className="w-4 h-4" /> Stop</button></>;
  return (
    <section data-testid="pb-local-setup" className="mt-6 border border-[#D9B47A]/20 p-6">
      <p className="text-[15px] text-[#CFC2B6] leading-relaxed">Sit facing each other with the phone between you, or side by side. Each of you follows your own ankh.</p>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {[0, 1].map((k) => <input key={k} data-testid={`pb-local-name-${k}`} value={names[k]} maxLength={24} onChange={(e) => setNames((n) => n.map((x, j) => (j === k ? e.target.value : x)))} placeholder={k ? "Partner two" : "Partner one"} className={field} />)}
      </div>
      <p className="mt-6 text-[13px] text-[#A8998C]">Rhythm</p>
      <RhythmPick value={rhythm} onChange={setRhythm} />
      <button data-testid="pb-local-begin" onClick={() => setStartAt(Date.now() + 4000)} className={`${gold} w-full mt-6`}>Begin together</button>
    </section>
  );
};

const Distance = () => {
  const navigate = useNavigate();
  const [rhythm, setRhythm] = useState("mirrored");
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get("code") ?? "");
  const [busy, setBusy] = useState("");
  const run = async (kind, e) => {
    e?.preventDefault();
    setBusy(kind);
    try {
      const { data } = kind === "start" ? await api.post("/breath/partner", { rhythm }) : await api.post("/breath/partner/join", { code });
      navigate(`/app/breath/partner/${data.code}`);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
      setBusy("");
    }
  };
  return (
    <>
      <section data-testid="pb-remote-create" className="mt-6 border border-[#D9B47A]/20 p-6">
        <p className="font-display text-2xl">Begin a session</p>
        <p className="mt-2 text-[14px] text-[#A8998C]">Each of you on your own phone, in the same room or across any distance, breathing on one shared clock.</p>
        <p className="mt-5 text-[13px] text-[#A8998C]">Rhythm</p>
        <RhythmPick value={rhythm} onChange={setRhythm} />
        <button data-testid="pb-remote-start" onClick={() => run("start")} disabled={!!busy} className={`${gold} w-full mt-6`}>{busy === "start" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Create session</button>
      </section>
      <form onSubmit={(e) => run("join", e)} className="mt-6 border border-[#D9B47A]/20 p-6">
        <p className="font-display text-2xl">Join your partner</p>
        <input data-testid="pb-remote-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} placeholder="Session code" className={`${field} mt-5 font-tt-mono tracking-[0.3em] uppercase`} />
        <button data-testid="pb-remote-join" disabled={!!busy || code.trim().length < 4} className={`${ghost} w-full mt-4`}>{busy === "join" && <Loader2 className="w-4 h-4 animate-spin" />} Join session</button>
      </form>
    </>
  );
};

// /app/breath/partner — choose side by side on one phone, or two phones with a code.
export function PartnerBreathLobby() {
  const [mode, setMode] = useState(new URLSearchParams(window.location.search).get("code") ? "distance" : "together");
  return (
    <Page testId="pb-lobby" eyebrow="Gate 05 · Partner breath" title={<>Ankhing the breath, <em className="text-[#D9B47A]">together.</em></>}>
      <Link to="/app/us" data-testid="pb-our-field" className="mt-6 inline-block text-[14px] text-[#E8838E] underline underline-offset-4">Open Our Field: your streak, maps and scheduled practices</Link>
      <div role="tablist" className="mt-8 grid grid-cols-2 gap-2">
        {[["together", "Side by side", Smartphone], ["distance", "Two phones", Users]].map(([k, l, I]) => (
          <button key={k} role="tab" aria-selected={mode === k} data-testid={`pb-mode-${k}`} onClick={() => setMode(k)} className={`flex items-center justify-center gap-2 rounded-full border py-3 text-[14px] transition-colors ${mode === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6]"}`}><I className="w-4 h-4" /> {l}</button>
        ))}
      </div>
      {mode === "together" ? <SideBySide /> : <Distance />}
      <Link to="/app/settings#partner" data-testid="pb-invite-link" className="mt-8 flex items-center gap-3 border border-[#E8838E]/30 bg-[#140910] p-5 text-[14px] text-[#CFC2B6] hover:border-[#E8838E]/70 transition-colors">
        <Gift className="w-5 h-5 shrink-0 text-[#E8838E]" /> <span>Breathing across two phones needs 13 Gates access for each of you. Share your partner invitation and they save 25%.</span>
      </Link>
    </Page>
  );
}

const Invite = ({ code }) => {
  const link = `${window.location.origin}/app/breath/partner?code=${code}`;
  const share = async () => {
    if (navigator.share) await navigator.share({ title: "13 Gates to Ecstasy", text: `Breathe the Ankh with me. Session code ${code}`, url: link }).catch(() => {});
    else {
      await navigator.clipboard?.writeText(link).catch(() => {});
      toast.success("Invitation link copied.");
    }
  };
  return (
    <div data-testid="pb-invite" className="mt-6 border border-[#E8838E]/30 bg-[#140910] p-6">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">Session code</p>
      <p data-testid="pb-session-code" className="mt-2 font-tt-mono text-4xl tracking-[0.35em] text-[#F2E8DF]">{code}</p>
      <div className="mt-5 flex gap-2">
        <button data-testid="pb-share" onClick={share} className={`${ghost} flex-1`}><Share2 className="w-4 h-4" /> Invite</button>
        <button data-testid="pb-copy" onClick={() => navigator.clipboard?.writeText(code).then(() => toast.success("Code copied.")).catch(() => {})} className={ghost} aria-label="Copy code"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

// /app/breath/partner/:code — two phones on one shared clock.
export function PartnerBreathSession() {
  const { code } = useParams();
  const navigate = useNavigate();
  const now = useNow();
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const offset = useRef(0);
  const load = useCallback(() => api.get(`/breath/partner/${code}`).then(({ data }) => {
    offset.current = new Date(data.server_now).getTime() - Date.now();
    setS(data);
  }).catch((err) => setError(errText(err.response?.data?.detail))), [code]);
  useEffect(() => {
    load();
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [load]);
  const elapsed = s?.started_at ? now + offset.current - new Date(s.started_at).getTime() : 0;
  const state = useBreathLife(s?.rhythm, elapsed, Boolean(s?.started_at));
  const control = async (body) => {
    try {
      const { data } = await api.post(`/breath/partner/${code}/control`, body);
      setS((x) => ({ ...x, ...data }));
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    }
  };
  if (error) return <Page testId="pb-error" title="Session closed."><p className="mt-4 text-[#CFC2B6]">{error}</p><button onClick={() => navigate("/app/breath/partner")} className={`${ghost} mt-6`}>Partner breath</button></Page>;
  if (!s) return <Page testId="pb-loading"><Loader2 className="mt-16 w-5 h-5 animate-spin text-[#D9B47A]" /></Page>;
  const names = [s.host_name, s.guest_name || "Your partner"];
  const again = <button data-testid="pb-again" onClick={() => navigate("/app/breath/partner?code=")} className={gold}>Another session</button>;
  if (state.done) return <Page testId="pb-session" eyebrow="Partner breath"><Done map={<SharedMap s={s} />}>{again}</Done></Page>;
  if (s.ended) return <Page testId="pb-ended" eyebrow="Partner breath" title="This session has closed."><div className="mt-8">{again}</div></Page>;
  return (
    <Page testId="pb-session" eyebrow={`Partner breath · ${s.rhythm === "mirrored" ? "Mirrored" : "In unison"}`} title={s.started_at ? null : <>Breathe <em className="text-[#D9B47A]">as one.</em></>}>
      <p data-testid="pb-presence" className="mt-5 flex items-center gap-2 text-[14px] text-[#CFC2B6]">
        <span className={`w-2.5 h-2.5 rounded-full ${s.guest_name ? "bg-[#7FCB8B] shadow-[0_0_10px_#7FCB8B]" : "bg-[#8F8176] animate-pulse"}`} />
        {s.guest_name ? `Breathing with ${s.is_host ? s.guest_name : s.host_name}` : "Waiting for your partner to join…"}
      </p>
      {s.started_at ? <Duo rhythm={s.rhythm} names={names} mine={s.is_host ? 0 : 1} elapsed={elapsed} /> : (
        <>
          {s.is_host && !s.guest_name && <Invite code={s.code} />}
          <p className="mt-8 text-[13px] text-[#A8998C]">Rhythm · either of you can choose</p>
          <RhythmPick value={s.rhythm} onChange={(rhythm) => control({ action: "rhythm", rhythm })} />
          <button data-testid="pb-begin" onClick={() => control({ action: "start" })} disabled={!s.guest_name} className={`${gold} w-full mt-6`}>Begin together</button>
        </>
      )}
      <button data-testid="pb-end" onClick={() => window.confirm("End this session for both of you?") && control({ action: "end" })} className="mt-10 inline-flex items-center gap-2 text-sm text-[#A8998C] underline underline-offset-4"><X className="w-4 h-4" /> End session</button>
    </Page>
  );
}
